import { useGetSessionToken } from '@homestake/shared/hooks';
import { batch } from '@legendapp/state';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { postApiGameCashoutMutation } from '../api/__generated__/@tanstack/react-query.gen';
import {
  cashoutAnimationState$,
  manualBetState$,
  ratAnimationState$,
  towersState$,
  winDisplayState$,
} from '../state';
import { freeBetState$ } from '../state/free-bet.state';
import { sessionState$ } from '../state/session.state';
import { walletState$ } from '../state/wallet.state';
import { showErrorNotification } from '../utils';
import { countRowsCleared } from '../utils/towers.utils';
import { useBackendErrorNotification } from './useBackendErrorNotification.hook';
import { useCashoutSound } from './useCashoutSound.hook';

export const useManualBetCashout = () => {
  const { t } = useTranslation();
  const { token } = useGetSessionToken();
  const { showBackendErrorNotification } = useBackendErrorNotification();

  // Sound effects
  const { playSound: playCashoutSound } = useCashoutSound();

  const mutation = useMutation({
    ...postApiGameCashoutMutation(),
    onSuccess: ({
      balance,
      betId,
      towersResults,
      win,
      winId,
      baseBonusResponseDto,
    }) => {
      // Play cashout sound and trigger animations
      playCashoutSound();

      batch(() => {
        freeBetState$.update(baseBonusResponseDto, true);

        ratAnimationState$.triggerAnimation('win');
        cashoutAnimationState$.triggerAnimation('cashout', 3000);
        towersState$.setAllBricks(towersResults ?? []);
        walletState$.setBalance(balance ?? 0);
        walletState$.transactions.push({
          win: Boolean(win),
          winId: winId ?? '',
          betId: betId ?? '',
        });
        manualBetState$.setIsBetting(false);
        // Show win display for cashout
        const bricks = towersState$.bricks.peek();
        const rowsCleared = countRowsCleared(bricks);
        const betAmount = manualBetState$.betAmount.peek();

        winDisplayState$.showWinDisplay({
          betAmount,
          rowsCleared,
          winAmount: win ?? 0,
        });
      });
    },
    onError: (error) => {
      showBackendErrorNotification(error);
    },
  });

  const handleCashout = useCallback(() => {
    if (!token) {
      showErrorNotification({
        title: t('notifications.errorTitle'),
        message: t('notifications.noTokenFoundMessage'),
        dataTestId: 'cashout-no-token',
      });
      return;
    }

    mutation.mutate({
      body: {
        gameRoundId: sessionState$.gameRoundId.peek(),
        token,
      },
    });
  }, [token, mutation, t]);

  return { mutation, handleCashout };
};
